import { useEffect, useMemo, useState } from "react";
import { Search, BookOpen } from "lucide-react";
import { Input } from "../components/ui/input";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "../components/ui/accordion";

const SECTIONS = [
  {
    id: "visibility-score",
    title: "The Visibility Score",
    body: "Your Visibility Score is a 0-100 weighted measure of how favorably AI models describe your brand: " +
      "Sentiment Score (35%) - the emotional valence of AI mentions; Mention Quality Score (40%) - accuracy, " +
      "specificity and prominence of the mention; Recommendation Strength (25%) - whether the model would " +
      "actively recommend you to a user. 70+ is a Strong Signal, 40-70 is a Contested Signal, below 40 is a " +
      "Signal Crisis requiring immediate Truth Statement deployment.",
  },
  {
    id: "sentiment",
    title: "Sentiment",
    body: "Sentiment measures the emotional tone of every AI-generated mention of your brand - positive, " +
      "neutral, or negative - across all four monitored platforms. A single negative mention rarely moves the " +
      "needle; a pattern across OpenAI, Claude, Gemini and Perplexity is what drives the score down.",
  },
  {
    id: "conflict",
    title: "Conflict (Sovereign Red Hallucination Warnings)",
    body: "A Conflict is any claim an AI model states about your brand that BrandSignal's hallucination " +
      "sub-agent flags as inaccurate, unverifiable, contradictory across models, or fabricated outright. Each " +
      "Conflict carries a severity (high / medium / low) and comes paired with a Truth Statement - the exact " +
      "correction needed to close the gap.",
  },
  {
    id: "truth-statements",
    title: "Executing Truth Statements",
    body: "A Truth Statement is a concise, copy-paste-ready factual correction. To execute one: (1) identify " +
      "the flagged claim and its severity, (2) publish the Truth Statement verbatim on your own site in plain " +
      "prose, (3) mirror it in JSON-LD structured data where relevant, (4) update your Wikipedia/Wikidata entry " +
      "if notable, (5) seek a press or review-site mention repeating the fact, (6) re-run an audit (or wait for " +
      "your Agency auto-rescan) to confirm the correction propagated. Perplexity and Gemini tend to reflect " +
      "corrections fastest; OpenAI and Claude can take longer to re-index.",
  },
  {
    id: "geo",
    title: "GEO: Generative Engine Optimization",
    body: "GEO is the discipline of optimizing your brand for AI answer engines instead of classic Google SEO. " +
      "Core tactics: publish clear, quotable, single-sentence factual claims; add schema.org / JSON-LD structured " +
      "data (Organization, Product, FAQPage); keep Wikipedia/Wikidata accurate (a primary source most models " +
      "retrieve from); publish an llms.txt signalling canonical facts to crawlers; keep NAP (Name/Address/" +
      "Positioning) data consistent everywhere; earn citations on high-authority, frequently-crawled domains; and " +
      "directly answer the exact questions users ask AI models about your category.",
  },
  {
    id: "rag",
    title: "Retrieval-Augmented Generation (RAG)",
    body: "RAG is how a model retrieves external documents at answer-time and grounds its response in them, " +
      "rather than relying only on frozen training data. This is why fixing a hallucination on a page a model's " +
      "retriever can access often corrects future answers faster than waiting for a full model retrain - " +
      "especially on RAG-native engines like Perplexity.",
  },
  {
    id: "four-platforms",
    title: "The 4 AI Platforms, Explained",
    body: "OpenAI (GPT): mostly training-data grounded, strong at general sentiment, can lag on recent events. " +
      "Anthropic Claude: similar grounding, tends to hedge more on unverified claims - which is why BrandSignal " +
      "uses Claude as its own scoring sub-agent. Google Gemini: tightly integrated with Google's live index and " +
      "Knowledge Graph, so Wikipedia/structured-data fixes propagate here fastest. Perplexity: RAG-native and " +
      "answer-engine-first, cites live web sources for nearly every answer and is the most sensitive to fresh " +
      "on-page corrections - and the most likely to name competitors.",
  },
  {
    id: "broadcast-tower",
    title: "The Broadcast Tower: How to Use Truth URLs to Recode the AI Signal",
    body: "Every audited brand gets a dedicated Broadcast Tower page at /truth/{brand-slug} - a clean, " +
      "server-rendered, AI-crawlable Source-of-Truth page listing every disputed claim paired with its Truth " +
      "Statement, marked up with schema.org ClaimReview structured data so crawlers can parse the correction " +
      "programmatically, not just as prose. Unlike the rest of the app, this page requires no login and no " +
      "JavaScript to read - AI crawlers (GPTBot, ClaudeBot, PerplexityBot, Google-Extended, and others) fetch raw " +
      "HTML, so the Tower is built to be readable by a plain HTTP GET. To use it: (1) open your audit result and " +
      "click 'View Broadcast Tower page', (2) copy the Tower URL shown at the top of that page, (3) publish that " +
      "URL as a backlink from your own site's footer or press page, submit it to relevant directories, and " +
      "reference it anywhere your brand is discussed online - the more authoritative pages link to it, the faster " +
      "AI crawlers re-index it and pick up the corrected facts. BrandSignal logs every crawler hit to a Tower page " +
      "(bot name, timestamp, brand) so you can see exactly when GPTBot, ClaudeBot, or PerplexityBot last ingested " +
      "your corrections - visible to the Founder in the Sovereign Directorate's Crawl Activity panel.",
  },
  {
    id: "identity-shield",
    title: "The Sovereign's Guide to Identity Shielding",
    body: "The Sovereign Identity Shield protects your named executives from synthetic fraud - voice clones and " +
      "deepfake video used for wire-fraud requests, fabricated statements, or impersonation scams. The ROI case: " +
      "a single successful executive voice-clone fraud attempt against a company routinely costs six or seven " +
      "figures in wired funds, and a viral deepfake video takes days to publicly debunk without a pre-established " +
      "verification channel - the Shield exists so that debunking takes seconds, not days. How it works: (1) " +
      "register your executive and enroll a 60-second Master Voice Sample plus a Master Face Sample - the audio " +
      "is stored securely for reference, but face media is analyzed in-memory only and immediately discarded, " +
      "never stored raw (a Face-Landmark Hash - the geometry of the face, not the image itself - is the only " +
      "thing kept); (2) share your public Verification Bridge link (/verify/your-executive) with your bank, " +
      "legal team, or the press so anyone receiving a suspicious call or clip can upload it and get an instant " +
      "clinical verdict - Maison-Verified Original, AI-Synthetic Potential, or Inconclusive; (3) issue a Public " +
      "Proof of Life link during any crisis to publicly timestamp that you are safe and authentic; (4) if a " +
      "high-confidence synthetic clip is ever detected, the Emergency Protocol automatically alerts your legal " +
      "team and the Sovereign Directorate by SMS. This is a best-effort forensic signal built on real numeric " +
      "biometric comparison (voice-print and face-geometry matching) combined with an AI clinical narrative - it " +
      "is not a certified or legal-grade deepfake determination, but it closes the gap from days to seconds.",
  },
];

export const Handbook = () => {
  const [query, setQuery] = useState("");
  const [openItems, setOpenItems] = useState([]);

  useEffect(() => {
    const hash = window.location.hash.replace("#", "");
    if (hash && SECTIONS.some((s) => s.id === hash)) {
      setOpenItems([hash]);
      setTimeout(() => {
        document.getElementById(hash)?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 150);
    }
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return SECTIONS;
    return SECTIONS.filter((s) => s.title.toLowerCase().includes(q) || s.body.toLowerCase().includes(q));
  }, [query]);

  return (
    <div className="mx-auto max-w-3xl px-6 py-16" data-testid="handbook-page">
      <div className="mb-3 flex items-center gap-2">
        <BookOpen className="h-6 w-6 text-sovereign" />
        <h1 className="font-heading text-4xl font-bold text-white">The Intelligence Handbook</h1>
      </div>
      <p className="mb-8 text-slate-400">The Sovereign's Guide to AI Visibility - metrics, conflicts, and how to execute Truth Statements.</p>

      <div className="relative mb-6">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search the handbook..."
          className="border-surface-border bg-obsidian pl-9 text-white"
          data-testid="handbook-search-input"
        />
      </div>

      {filtered.length === 0 ? (
        <p className="text-slate-500" data-testid="handbook-no-results">No sections match your search.</p>
      ) : (
        <Accordion
          type="multiple"
          value={openItems}
          onValueChange={setOpenItems}
          className="rounded-lg border border-surface-border bg-surface-card px-5"
          data-testid="handbook-sections"
        >
          {filtered.map((s) => (
            <AccordionItem key={s.id} value={s.id} id={s.id} className="border-surface-border" data-testid={`handbook-section-${s.id}`}>
              <AccordionTrigger className="font-heading text-white hover:no-underline">{s.title}</AccordionTrigger>
              <AccordionContent className="text-slate-400">{s.body}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      )}
    </div>
  );
};
